import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LegalModal } from './LegalPages';
import { 
  User, 
  Envelope, 
  Lock, 
  Buildings, 
  ArrowRight, 
  CircleNotch,
  CheckCircle,
  Briefcase,
  ShieldCheck,
  Receipt,
  ClipboardText,
  ChartLineUp,
  Users,
  CurrencyDollar,
  Warehouse,
  CalendarCheck,
  FileText,
  Cloud,
  Clock,
  LockKey,
  UsersThree,
  CheckSquare,
  Square,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    company_name: '',
    country: 'UAE',
    currency: 'AED',
    industry: '',
    phone: '',
    num_clients: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAccountantMode, setIsAccountantMode] = useState(searchParams.get('mode') === 'accountant');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState('terms');
  const { register } = useAuth();
  const navigate = useNavigate();

  const openLegalModal = (type) => {
    setLegalModalType(type);
    setLegalModalOpen(true);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('Please agree to the Terms & Conditions, Privacy Policy, and Disclaimer to continue.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      // Record consent
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/legal/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terms_version: '1.0',
          privacy_version: '1.0',
          disclaimer_version: '1.0'
        })
      });
      
      await register(formData);
      if (isAccountantMode) {
        navigate('/dashboard/accountant');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // SME Features for grid
  const smeFeatureCards = [
    { icon: Users, label: 'HR Management', color: '#3b82f6' },
    { icon: CurrencyDollar, label: 'Accounts & Finance', color: '#10b981' },
    { icon: ChartLineUp, label: 'Sales & CRM', color: '#f59e0b' },
    { icon: Warehouse, label: 'Inventory', color: '#ec4899' },
  ];

  // Accountant Features for grid
  const accountantFeatureCards = [
    { icon: Buildings, label: 'Multi-Client Dashboard', color: '#d4a84b' },
    { icon: Receipt, label: 'VAT Filing', color: '#10b981' },
    { icon: ShieldCheck, label: 'Tax Compliance', color: '#8b5cf6' },
    { icon: ClipboardText, label: 'Bulk Reports', color: '#3b82f6' },
  ];

  // SME Benefits
  const smeBenefits = [
    'Accounting & payroll management',
    'Invoicing & sales tracking',
    'Compliance tracking & alerts',
    'Reports & analytics dashboard',
    'Document storage & management',
    'Multi-company support',
  ];

  // Accountant Benefits
  const accountantBenefits = [
    'Multi-company management',
    'Bulk VAT filing & reporting',
    'Tax compliance safety engine',
    'Deadline tracking & alerts',
    'Client document hub',
    'Enterprise analytics dashboard',
  ];

  const featureCards = isAccountantMode ? accountantFeatureCards : smeFeatureCards;
  const benefits = isAccountantMode ? accountantBenefits : smeBenefits;

  return (
    <div className="min-h-screen flex bg-[#0a1628]">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      {/* LEFT PANEL - Form Area */}
      <div className="w-full lg:w-[52%] flex items-center justify-center p-6 sm:p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-lg space-y-6 animate-fade-in">
          
          {/* Logo Section */}
          <div className="text-center mb-8">
            <div className="inline-block">
              {/* Logo */}
              <div className="flex items-center justify-center gap-3 mb-3">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isAccountantMode 
                    ? 'bg-gradient-to-br from-[#d4a84b] to-[#b8923a]' 
                    : 'bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f]'
                }`}>
                  {isAccountantMode ? (
                    <ShieldCheck size={28} className="text-[#0f172a]" weight="fill" />
                  ) : (
                    <ChartLineUp size={28} className="text-[#d4a84b]" weight="fill" />
                  )}
                </div>
                {/* Text Logo */}
                <div>
                  <h1 className="text-3xl font-black tracking-tight">
                    <span className="text-white font-black">BIZ</span>
                    <span className="text-white italic font-medium">Core</span>
                    {isAccountantMode && (
                      <span className="text-[#d4a84b] font-normal ml-1">Pro</span>
                    )}
                    <span className="text-[#d4a84b] text-sm align-super ml-0.5">™</span>
                  </h1>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                by <span className="font-semibold text-white">SGK</span>
                <span className="font-semibold text-[#d4a84b]"> Apps</span>
                <span className="text-slate-500">™</span>
              </p>
            </div>
          </div>

          {/* Dynamic Headline */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {isAccountantMode 
                ? 'Manage Multiple Clients with Ease' 
                : 'Run Your Business & Stay Compliant'}
            </h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-md mx-auto">
              {isAccountantMode 
                ? 'VAT, corporate tax, and multi-client management in one dashboard' 
                : 'Accounting, compliance, and operations in one powerful system'}
            </p>
          </div>

          {/* Account Type Toggle */}
          <div className="space-y-3">
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-400 text-center">
              Select Account Type
            </label>
            <div className="flex justify-center">
              <div className="inline-flex rounded-2xl bg-[#1e293b]/80 p-1.5 border border-slate-700/50 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setIsAccountantMode(false)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    !isAccountantMode 
                      ? 'bg-white shadow-lg text-[#1e3a5f]' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-sme"
                >
                  <Buildings size={18} weight={!isAccountantMode ? 'fill' : 'regular'} />
                  <span>Business (SME)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsAccountantMode(true)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isAccountantMode 
                      ? 'bg-gradient-to-r from-[#d4a84b] to-[#c49a3d] shadow-lg text-[#0f172a]' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-accountant"
                >
                  <Briefcase size={18} weight={isAccountantMode ? 'fill' : 'regular'} />
                  <span>Accountant (Pro)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div 
              className="p-4 rounded-xl text-sm bg-red-500/10 text-red-400 border border-red-500/20 backdrop-blur-sm"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                  First Name
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                    required
                    data-testid="register-firstname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                  Last Name
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                    required
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                Email Address
              </Label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a secure password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-11 pr-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#d4a84b] p-1 rounded transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Business/Firm Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                {isAccountantMode ? 'Accounting Firm Name' : 'Business Name'}
              </Label>
              <div className="relative">
                <Buildings className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder={isAccountantMode ? "Your Accounting Firm" : "Your Business Name"}
                  value={formData.company_name}
                  onChange={handleChange}
                  className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                  required
                  data-testid="register-company-input"
                />
              </div>
            </div>

            {/* Country + Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="country" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                  Country / Region
                </Label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={(e) => {
                    const c = e.target.value;
                    // Auto-pick the natural currency for that region.
                    const ccyMap = { UAE: 'AED', INDIA: 'INR', UK: 'GBP', USA: 'USD', SAUDI_ARABIA: 'SAR', SINGAPORE: 'SGD' };
                    setFormData(prev => ({ ...prev, country: c, currency: ccyMap[c] || prev.currency }));
                  }}
                  className="w-full h-12 px-4 rounded-xl bg-[#1e293b]/60 border border-slate-700/50 text-white focus:border-[#d4a84b] focus:outline-none"
                  data-testid="register-country-select"
                >
                  <option value="UAE">United Arab Emirates</option>
                  <option value="INDIA">India</option>
                  <option value="SAUDI_ARABIA">Saudi Arabia</option>
                  <option value="UK">United Kingdom</option>
                  <option value="USA">United States</option>
                  <option value="SINGAPORE">Singapore</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                  Currency
                </Label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-xl bg-[#1e293b]/60 border border-slate-700/50 text-white focus:border-[#d4a84b] focus:outline-none"
                  data-testid="register-currency-select"
                >
                  <option value="AED">AED — UAE Dirham</option>
                  <option value="INR">INR — Indian Rupee</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — British Pound</option>
                  <option value="SAR">SAR — Saudi Riyal</option>
                  <option value="SGD">SGD — Singapore Dollar</option>
                </select>
              </div>
            </div>

            {/* Number of Clients (Accountant Only) */}
            {isAccountantMode && (
              <div className="space-y-2">
                <Label htmlFor="num_clients" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                  Number of Clients <span className="text-slate-500 normal-case">(optional)</span>
                </Label>
                <div className="relative">
                  <UsersThree className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="num_clients"
                    name="num_clients"
                    type="number"
                    placeholder="e.g., 10"
                    value={formData.num_clients}
                    onChange={handleChange}
                    className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                    min="1"
                    data-testid="register-clients-input"
                  />
                </div>
              </div>
            )}

            {/* Terms Consent Checkbox */}
            <div className="space-y-2">
              <div 
                className="flex items-start gap-3 cursor-pointer group"
                onClick={() => setAgreedToTerms(!agreedToTerms)}
              >
                <div className="mt-0.5">
                  {agreedToTerms ? (
                    <CheckSquare size={22} className="text-[#d4a84b]" weight="fill" />
                  ) : (
                    <Square size={22} className="text-slate-500 group-hover:text-slate-400" />
                  )}
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLegalModal('terms'); }}
                    className="text-[#d4a84b] hover:text-white underline underline-offset-2 transition-colors"
                  >
                    Terms & Conditions
                  </button>
                  ,{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLegalModal('privacy'); }}
                    className="text-[#d4a84b] hover:text-white underline underline-offset-2 transition-colors"
                  >
                    Privacy Policy
                  </button>
                  , and{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLegalModal('disclaimer'); }}
                    className="text-[#d4a84b] hover:text-white underline underline-offset-2 transition-colors"
                  >
                    Disclaimer
                  </button>
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className={`w-full h-14 font-semibold text-base transition-all duration-300 rounded-xl shadow-lg ${
                agreedToTerms 
                  ? 'bg-gradient-to-r from-[#d4a84b] to-[#c49a3d] hover:from-[#e5b95c] hover:to-[#d4a84b] shadow-[#d4a84b]/20 hover:shadow-xl hover:shadow-[#d4a84b]/30 hover:-translate-y-0.5'
                  : 'bg-slate-600 cursor-not-allowed'
              }`}
              style={{ color: agreedToTerms ? '#0f172a' : '#94a3b8' }}
              disabled={loading || !agreedToTerms}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <CircleNotch className="animate-spin" size={22} />
              ) : (
                <>
                  {isAccountantMode ? 'Create Pro Account' : 'Create Business Account'}
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center text-sm text-slate-400">
            Already have an account?{' '}
            <Link 
              to={isAccountantMode ? "/login?mode=accountant" : "/login"}
              className="font-semibold text-[#d4a84b] hover:text-white transition-colors"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>

          {/* Footer Trust Indicators */}
          <div className="flex items-center justify-center gap-6 pt-4 border-t border-slate-700/30">
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <LockKey size={14} className="text-[#d4a84b]" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <Cloud size={14} className="text-[#d4a84b]" />
              <span>Cloud-Based</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <ShieldCheck size={14} className="text-[#d4a84b]" />
              <span>UAE Compliance Ready</span>
            </div>
          </div>

          {/* Website Footer */}
          <div className="text-center pt-4">
            <a 
              href="https://bizcore.sgkapps.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-[#d4a84b] transition-colors"
            >
              bizcore.sgkapps.com
            </a>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - Value Proposition */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden">
        {/* Gradient Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: isAccountantMode 
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
              : 'linear-gradient(135deg, #1e3a5f 0%, #234b70 50%, #2d5a80 100%)'
          }}
        />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #d4a84b 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-10 xl:px-14 py-12 w-full">
          
          {/* Mode Badge */}
          {isAccountantMode && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4a84b]/20 text-[#d4a84b] text-xs font-semibold mb-6 w-fit">
              <Briefcase size={14} weight="fill" />
              ACCOUNTANT EDITION
            </div>
          )}

          {/* Title */}
          <h2 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
            {isAccountantMode 
              ? <>Manage Multiple Clients<br />with Ease</>
              : <>Start Your Business<br />Journey</>}
          </h2>
          
          <p className={`text-base mb-8 ${isAccountantMode ? 'text-slate-300' : 'text-blue-100'}`}>
            {isAccountantMode 
              ? 'Professional tools for accounting firms and tax consultants'
              : 'Join businesses managing their operations with BIZCore™'}
          </p>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {featureCards.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 backdrop-blur-sm rounded-xl p-4 border transition-all duration-300 hover:scale-[1.02] cursor-default"
                style={{
                  background: isAccountantMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                  borderColor: isAccountantMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.15)'
                }}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon size={22} style={{ color: feature.color }} weight="duotone" />
                </div>
                <span className="text-white text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>

          {/* Benefits List */}
          <div className="space-y-3 mb-10">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#d4a84b]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={14} className="text-[#d4a84b]" weight="fill" />
                </div>
                <span className={`text-sm ${isAccountantMode ? 'text-slate-300' : 'text-blue-100'}`}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>

          {/* Trust Stats */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Cloud size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">100%</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Cloud-Based</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Clock size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">24/7</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Availability</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <LockKey size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">SSL</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Secured</div>
            </div>
          </div>

          {/* Bottom Branding */}
          <div className="mt-auto pt-8">
            <div className={`flex items-center gap-2 text-sm ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>
              <div className="w-8 h-[1px] bg-current opacity-30"></div>
              <span>{isAccountantMode ? 'For accounting professionals' : 'Trusted by businesses'}</span>
            </div>
            <div className="mt-6 text-center">
              <a 
                href="https://bizcore.sgkapps.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-slate-400 hover:text-[#d4a84b] transition-colors"
              >
                bizcore.sgkapps.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal 
        isOpen={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        type={legalModalType}
        onAccept={() => {
          setLegalModalOpen(false);
          setAgreedToTerms(true);
        }}
      />
    </div>
  );
}
